import {
  AutocompleteInputChangeReason,
  Box,
  debounce,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { isFulfilled } from "@reduxjs/toolkit";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { useAppDispatch } from "../../../app/hooks";
import AmountField from "../../../common/components/functional/AmountField";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import InputHelperText from "../../../common/components/functional/error/InputHelperText";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { getName, sortByName } from "../../../common/helpers/common";
import isEmpty from "../../../common/helpers/isEmpty";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { Nullable } from "../../../common/types/nullable";
import { AutomatchingDelayWarning } from "../AutomatchingDelayWarning";
import { OFFER_FIRST_TO_OWN, OFFER_TO_OWN } from "../constants";
import { TalentNameWithPreview } from "../editOrder/long/talentsStatus/TalentNameWithPreview";
import { getBundleRowValues } from "../newOrder/short/orderFormShortRowHandlers";
import { ShiftOrderContext } from "../newOrder/short/OrderShortDataProvider";
import {
  ManualOfferSearchRequirements,
  WorkLocationPoolTalent,
} from "../orderAPI";
import { parseToInterval } from "../OrderTimeField";
import { Automatching, AutomatchingStatus, OrderFields } from "../orderTypes";
import ToggleAutomatching from "../ToggleAutomatching";
import { TalentPool } from "../types/common";
import {
  automatchingIsNotInThePast,
  checkIsDelayTooLong,
  getEarliestShiftStart,
  shouldValidateDelay,
} from "../utils";
import {
  fetchWorkLocationTalentPoolById,
  findManualOfferTalents,
} from "../workOffersSlice";
import { PoolTalentsInfo } from "./PoolTalentsInfo";

interface Props {
  isOpen: boolean;
  closeDialog: () => void;
  bundleId?: string;
  workLocationId: string;
  handleManualOfferingChange: (
    automatching: Automatching,
    selectedPool: Nullable<TalentPool>,
  ) => void;
  values: OrderFields;
}

interface ShiftManualOfferFormValues {
  talents: AutocompleteType[];
  automatchingStatus: AutomatchingStatus;
  automatchingStartDelayMinutes: number | null;
}

const initializePoolOfferTarget = (automatchingStatus: AutomatchingStatus) => {
  return automatchingStatus === AutomatchingStatus.Off
    ? OFFER_TO_OWN
    : OFFER_FIRST_TO_OWN;
};

export const ShiftManualOfferDialog = ({
  isOpen,
  closeDialog,
  handleManualOfferingChange,
  values,
  bundleId,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["common", "order"]);

  const { orderRows } = useContext(ShiftOrderContext);
  const orderedAmount = values.amount;

  const [talentSearchString, setTalentSearchString] = useState("");
  const [suggestedTalents, setSuggestedTalents] = useState<AutocompleteType[]>(
    [],
  ); // TODO: change type, waiting for backend implementation

  const [selectedPool, setSelectedPool] = useState<TalentPool | null>(
    values.automatching.pool,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasPool, setHasPool] = useState(false);
  const [poolTalents, setPoolTalents] = useState<WorkLocationPoolTalent[]>([]);
  const [isPoolLoading, setIsPoolLoading] = useState<boolean>(false);
  const [poolOfferTarget, setPoolOfferTarget] = useState<Nullable<string>>(
    initializePoolOfferTarget(values.automatching.automatchingStatus),
  );

  const getManualOfferSearchParams =
    useCallback((): ManualOfferSearchRequirements => {
      let schedule = "";

      if (bundleId) {
        // get start and end date time values from bundle
        const bundleValues = getBundleRowValues(orderRows, bundleId);

        if (bundleValues) {
          schedule = processBundleDates(bundleValues);
        }
      } else {
        const startDate = new Date(values.date as Date);
        const { startDateTime, endDateTime } = parseToInterval(
          values.time,
          startDate,
        );

        const start = new Date(startDateTime).toISOString().split(".")[0];
        const end = new Date(endDateTime).toISOString().split(".")[0];
        schedule = `${start};${end}`;
      }

      // When user has not yet selected whether to offer to pool or not,
      // the preliminary search goes to pool to determine if there are any talents available
      // (and if the pool option should be disabled)
      const searchFromPool =
        selectedPool === null
          ? true
          : selectedPool === TalentPool.WORK_LOCATION;

      const template = values.orderTemplate;
      const params: ManualOfferSearchRequirements = {
        search: talentSearchString,
        workLocationId: values.workLocation.id,
        workTitleId: template.workTitleId,
        tesId: template.tes.id,
        languages: template.languages
          .map((language) => `${language.id};${language.skillLevel}`)
          .join(","),
        certificates: template.certificates
          .map((certificate) => certificate.id)
          .toString(),
        trainings: template.trainings.map((training) => training.id).toString(),
        salaryLimit: template.salaryLimit || undefined,
        schedule,
        fromWorkLocationTalentPool: searchFromPool,
      };

      return params;
    }, [
      talentSearchString,
      values.date,
      values.orderTemplate,
      values.time,
      values.workLocation.id,
      bundleId,
      orderRows,
      selectedPool,
    ]);

  useEffect(() => {
    setIsPoolLoading(true);
    dispatch(
      fetchWorkLocationTalentPoolById({
        workLocationId: values.workLocation.id,
      }),
    )
      .then((action) => {
        const poolExists = isFulfilled(action) && !!action.payload;
        setHasPool(poolExists);
        if (!poolExists) {
          setSelectedPool(TalentPool.MANUAL);
        } else {
          // Fetch available talents with requirements
          const poolTalents = action.payload.talents || [];
          dispatch(findManualOfferTalents(getManualOfferSearchParams())).then(
            (action) => {
              if (isFulfilled(action)) {
                const availableTalents = poolTalents.filter((poolTalent) =>
                  action.payload.find(
                    (availableTalent) => availableTalent.id === poolTalent.id,
                  ),
                );
                setPoolTalents(availableTalents);
                // Make sure unavailable work location pool cannot be selected
                if (
                  isEmpty(action.payload) &&
                  selectedPool === TalentPool.WORK_LOCATION
                ) {
                  setSelectedPool(TalentPool.MANUAL);
                }
              }
            },
          );
        }
      })
      .finally(() => setIsPoolLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.workLocation.id, dispatch, setSelectedPool, selectedPool]);

  const { control, handleSubmit, setValue, watch, reset } =
    useForm<ShiftManualOfferFormValues>({
      defaultValues: {
        talents: values.automatching.talents,
        automatchingStatus: values.automatching.automatchingStatus,
        automatchingStartDelayMinutes:
          values.automatching.automatchingStartDelayMinutes || 30,
      },
      mode: "onChange",
    });
  const delayMinutes = watch("automatchingStartDelayMinutes");
  const automatchingStatus = watch("automatchingStatus");
  const isAutomatchingOn = automatchingStatus === AutomatchingStatus.On;

  const handleSelectPool = (pool: TalentPool) => {
    reset();
    setPoolOfferTarget(
      initializePoolOfferTarget(values.automatching.automatchingStatus),
    );
    setSelectedPool(pool);
    if (pool === TalentPool.MANUAL) {
      setValue("talents", [], { shouldDirty: true });
    } else {
      setValue(
        "talents",
        poolTalents.map((talent) => ({
          id: talent.id,
          name: getName(talent, "firstNameFirst"),
        })),
        { shouldDirty: true },
      );
    }
  };

  const handleOfferToOwnPool = () => {
    setPoolOfferTarget(OFFER_TO_OWN);
    setValue("automatchingStatus", AutomatchingStatus.Off, {
      shouldDirty: true,
    });
    setValue("automatchingStartDelayMinutes", null);
  };

  const handleOfferFirstToOwnPool = () => {
    setPoolOfferTarget(OFFER_FIRST_TO_OWN);
    setValue("automatchingStatus", AutomatchingStatus.On, {
      shouldDirty: true,
    });
    setValue("automatchingStartDelayMinutes", 30);
  };

  const onInputChange = debounce(
    (term: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setTalentSearchString(term);
      }
    },
    400,
  );

  const processBundleDates = (bundleValues: OrderFields[]) => {
    let dateTimeIntervals: string[] = [];

    bundleValues.forEach((bundle) => {
      const startDate = new Date(bundle.date as Date);
      const { startDateTime, endDateTime } = parseToInterval(
        bundle.time,
        startDate,
      );

      const start = new Date(startDateTime).toISOString().split(".")[0];
      const end = new Date(endDateTime).toISOString().split(".")[0];
      dateTimeIntervals.push(`${start};${end}`);
    });

    return dateTimeIntervals.join(",");
  };

  useEffect(() => {
    setIsLoading(true);
    if (selectedPool === TalentPool.MANUAL) {
      dispatch(findManualOfferTalents(getManualOfferSearchParams())).then(
        (action) => {
          setIsLoading(false);
          if (isFulfilled(action)) {
            const formattedTalents = sortByName(action.payload).map(
              (talent) => ({
                id: talent.id,
                name: getName(talent),
              }),
            );
            setSuggestedTalents(formattedTalents);
          }
        },
      );
    }
  }, [dispatch, getManualOfferSearchParams, selectedPool]);

  const onSave = (data: FieldValues) => {
    const isOff = data.automatchingStatus === AutomatchingStatus.Off;
    const automatching = {
      automatchingStatus: data.automatchingStatus,
      talents: data.talents,
      automatchingStartDelayMinutes: isOff
        ? null
        : data.automatchingStartDelayMinutes,
      pool: selectedPool,
    };

    handleManualOfferingChange(automatching, selectedPool);
    closeDialog();
  };

  const earliestShiftStart = getEarliestShiftStart(orderRows, bundleId, values);
  const triesToAutomatchInThePast =
    isAutomatchingOn && !automatchingIsNotInThePast(earliestShiftStart);
  const shouldValidateAutomatchDelay = shouldValidateDelay(
    selectedPool,
    poolOfferTarget,
    automatchingStatus,
    triesToAutomatchInThePast,
  );
  const automatchingDelayError = checkIsDelayTooLong(
    delayMinutes,
    earliestShiftStart,
    shouldValidateAutomatchDelay,
  );
  // Disable if "offer to pool" is selected but no target is selected.
  // In "offer to selected talents" every choice combination is fine.
  const confirmDisabled =
    (selectedPool === TalentPool.WORK_LOCATION && !poolOfferTarget) ||
    triesToAutomatchInThePast ||
    automatchingDelayError;

  return (
    <BasicDialog
      open={isOpen}
      onCancel={() => closeDialog()}
      onConfirm={handleSubmit(onSave)}
      title={
        t("order:automatchingDialogContentTitle") ||
        "order:automatchingDialogContentTitle"
      }
      PaperProps={{ sx: { minWidth: "640px" } }}
      confirmDisabled={confirmDisabled}
      noBorder
      content={
        <Box display="flex" flexDirection="column">
          {isPoolLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <LoadingSpinner />
            </Box>
          ) : (
            <>
              {hasPool && (
                <Box
                  sx={{
                    backgroundColor: "#F5F5F5",
                    padding: "0.5rem 1rem",
                    borderRadius: "4px",
                  }}
                >
                  <FormControlLabel
                    name={TalentPool.MANUAL}
                    control={
                      <Radio
                        checked={selectedPool === TalentPool.MANUAL}
                        onChange={() => handleSelectPool(TalentPool.MANUAL)}
                      />
                    }
                    label={t("order:offerToSelectedTalents")}
                  />
                  <FormControlLabel
                    name={TalentPool.WORK_LOCATION}
                    control={
                      <Radio
                        checked={selectedPool === TalentPool.WORK_LOCATION}
                        disabled={isEmpty(poolTalents)}
                        onChange={() =>
                          handleSelectPool(TalentPool.WORK_LOCATION)
                        }
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap="0.5rem">
                        <Typography
                          color={(theme) =>
                            isEmpty(poolTalents)
                              ? theme.palette.text.disabled
                              : theme.palette.text.primary
                          }
                        >
                          {t("order:offerToPool")}
                        </Typography>
                        {isEmpty(poolTalents) && (
                          <Typography variant="secondary" fontSize={12}>
                            {t("order:poolNotAvailableHelper")}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Box>
              )}

              {selectedPool === TalentPool.WORK_LOCATION && (
                <Box
                  display="flex"
                  flexDirection="column"
                  mt="1rem"
                  gap="0.5rem"
                >
                  <PoolTalentsInfo
                    talentCount={poolTalents.length}
                    orderedAmount={orderedAmount}
                  />
                  <FormControlLabel
                    name="offerToOwnPool"
                    control={
                      <Radio
                        checked={poolOfferTarget === OFFER_TO_OWN}
                        onChange={handleOfferToOwnPool}
                      />
                    }
                    label={t("order:offerToOwnPool")}
                    disabled={!poolTalents.length}
                  />
                  <FormControlLabel
                    name="offerFirstToOwnPool"
                    control={
                      <Radio
                        checked={poolOfferTarget === OFFER_FIRST_TO_OWN}
                        onChange={handleOfferFirstToOwnPool}
                        sx={{ marginTop: "-1.4rem" }}
                      />
                    }
                    label={
                      <>
                        {t("order:offerFirstToOwnPool")}
                        <InputHelperText
                          errorMessage={
                            triesToAutomatchInThePast
                              ? t("order:cannotAutomatchInPast")
                              : undefined
                          }
                          helperText={
                            isAutomatchingOn && !triesToAutomatchInThePast
                              ? t("order:automatchingHelperText", {
                                  minutes: delayMinutes,
                                })
                              : "\u00A0"
                          }
                          TypographyProps={{
                            style: { marginLeft: "0px" },
                          }}
                        />
                      </>
                    }
                  />
                  {isAutomatchingOn && !triesToAutomatchInThePast && (
                    <Controller
                      name="automatchingStartDelayMinutes"
                      control={control}
                      rules={{
                        required: true,
                        min: 1,
                        max: 99999,
                      }}
                      render={({ field }) => (
                        <>
                          <Box
                            mt={1}
                            display="flex"
                            alignItems="center"
                            gap="16px"
                          >
                            <Typography
                              variant="header"
                              fontWeight={600}
                              noWrap
                              minWidth={150}
                            >
                              {t("order:automatchingDelayMinutes")}
                            </Typography>
                            <AmountField
                              sx={{ width: "140px" }}
                              size="small"
                              value={field.value || 30}
                              onChange={(value: number) =>
                                field.onChange(Math.max(1, value))
                              }
                              min={1}
                              max={99999}
                              error={automatchingDelayError}
                            />
                          </Box>
                          {automatchingDelayError && (
                            <AutomatchingDelayWarning />
                          )}
                        </>
                      )}
                    />
                  )}
                </Box>
              )}

              {selectedPool === TalentPool.MANUAL && (
                <Box display="flex" flexDirection="column" gap="1rem" mt="1rem">
                  <Controller
                    name="talents"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Box display="flex" alignItems="center" gap="16px">
                        <Typography
                          variant="header"
                          fontWeight={600}
                          noWrap
                          minWidth={150}
                        >
                          {t("order:chooseTalent")}
                        </Typography>
                        <CustomAutocompleteMulti
                          fullWidth
                          disableClientFilter
                          isLoading={isLoading}
                          name="customerUserWorkLocationIds"
                          options={suggestedTalents}
                          onInputChange={(_, value, reason) =>
                            onInputChange(value, reason)
                          }
                          defaultValue={value}
                          onChange={(_: any, newValue: AutocompleteType[]) =>
                            onChange(newValue)
                          }
                          labelLink={(option) =>
                            `/home/talents/${option.id}/profile`
                          }
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <TalentNameWithPreview
                                talentId={option.id}
                                fullName={option.name}
                                disableLink
                              />
                            </li>
                          )}
                        />
                      </Box>
                    )}
                  />
                  <Controller
                    name="automatchingStatus"
                    control={control}
                    render={({ field }) => (
                      <>
                        <ToggleAutomatching
                          checked={field.value === AutomatchingStatus.Off}
                          automatchingStartDelayMinutes={delayMinutes}
                          showHelperText={false}
                          onChange={() => {
                            const newValue =
                              field.value === AutomatchingStatus.On
                                ? AutomatchingStatus.Off
                                : AutomatchingStatus.On;
                            field.onChange(newValue);
                            if (newValue === AutomatchingStatus.Off) {
                              setValue("automatchingStartDelayMinutes", null);
                            } else if (newValue === AutomatchingStatus.On) {
                              setValue("automatchingStartDelayMinutes", 30);
                            }
                          }}
                        />
                        <InputHelperText
                          errorMessage={
                            triesToAutomatchInThePast
                              ? t("order:cannotAutomatchInPast")
                              : undefined
                          }
                          TypographyProps={{
                            style: { marginTop: "-20px", marginLeft: "30px" },
                          }}
                        />
                      </>
                    )}
                  />
                  {isAutomatchingOn && !triesToAutomatchInThePast && (
                    <Controller
                      name="automatchingStartDelayMinutes"
                      control={control}
                      rules={{
                        required: true,
                        min: 1,
                        max: 99999,
                      }}
                      render={({ field }) => (
                        <Box>
                          <Box
                            display="flex"
                            alignItems="flex-start"
                            gap="16px"
                            marginBottom="8px"
                          >
                            <Typography
                              variant="header"
                              fontWeight={600}
                              noWrap
                              minWidth={150}
                              marginTop="8px"
                            >
                              {t("order:automatchingDelayMinutes")}
                            </Typography>
                            <AmountField
                              sx={{ width: "140px" }}
                              size="small"
                              value={field.value || 30}
                              onChange={(value: number) =>
                                field.onChange(Math.max(1, value))
                              }
                              min={1}
                              max={99999}
                              error={automatchingDelayError}
                            />
                          </Box>
                          {automatchingDelayError && (
                            <AutomatchingDelayWarning />
                          )}
                          <Typography fontWeight={600}>
                            {t("order:automatchingHelperText", {
                              minutes: delayMinutes,
                            })}
                          </Typography>
                        </Box>
                      )}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      }
    />
  );
};
